import * as React from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import Box from "@mui/material/Box";
import { itIT } from '@mui/x-data-grid/locales';

const paginationModel = { page: 0, pageSize: 25 };

export default function TableSnapshotDepositiAdmin({ val = [], campaignName }) {
  // columns con accesso SAFE: niente destructuring ({ value }) che rompe quando params è null
  const columns = React.useMemo(() => [
    { field: 'id', headerName: 'RowID', width: 80 },
    { field: 'garage', headerName: 'Deposito', width: 320, flex: 1, minWidth: 220 },
    { field: 'regione', headerName: 'Regione', width: 140 },
    { field: 'citta', headerName: 'Città', width: 140 },

    {
      field: 'completed', headerName: 'Completati', width: 130, headerAlign: 'center', align: 'center',
      renderCell: (params) => (
        <Box sx={{ backgroundColor: '#d0f2d5', width: "100%", height: "100%",
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {params?.value ?? '—'}
        </Box>
      ),
    },
    {
      field: 'inProgress', headerName: 'In Progress', width: 130, headerAlign: 'center', align: 'center',
      renderCell: (params) => (
        <Box sx={{ backgroundColor: '#fff6c2', width: "100%", height: "100%",
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {params?.value ?? '—'}
        </Box>
      ),
    },
    {
      field: 'nonAvviati', headerName: 'Non Avviati', width: 130, headerAlign: 'center', align: 'center',
      renderCell: (params) => (
        <Box sx={{ backgroundColor: '#fddede', width: "100%", height: "100%",
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {params?.value ?? '—'}
        </Box>
      ),
    },
    {
      field: 'total', headerName: 'Totale', width: 110, headerAlign: 'center', align: 'center',
      renderCell: (params) => (
        <Box sx={{ backgroundColor: '#e6f22694', width: "100%", height: "100%",
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {params?.value ?? '—'}
        </Box>
      ),
    },

    // % prende 'media' (0–100) se c'è; altrimenti usa 'percentage' (0–1)
    {
      field: 'percentage', headerName: '% Completamento', width: 180, headerAlign: 'center', align: 'center',
      valueFormatter: (params) => {
        const fromMedia = typeof params?.row?.media === 'number' ? params.row.media : null;        // 0–100
        const fromRatio = typeof params?.value === 'number' ? (params.value * 100) : null;         // 0–1 → %
        const v = fromMedia ?? fromRatio;
        return typeof v === 'number' ? `${v.toFixed(1)}%` : '—';
      }
    },

    // opzionale: totale auto dal backend (totAuto / numero_auto aggregato)
    {
      field: 'totAuto', headerName: 'Auto', width: 100, headerAlign: 'center', align: 'center',
      renderCell: (params) => params?.value ?? '—',
    },
  ], []);

  // mapping compatibile: se non ci sono numeri per riga, deriviamo dai 'stato' e mettiamo total=1
  const rows = React.useMemo(() => {
    return (Array.isArray(val) ? val : []).map((item, index) => {
      const hasNumeric =
        typeof item?.completati === 'number' ||
        typeof item?.inProgress === 'number' ||
        typeof item?.nonAvviati === 'number' ||
        typeof item?.totale === 'number';

      const completed  = hasNumeric ? (item?.completati ?? 0) : (item?.stato === 'completed' ? 1 : 0);
      const inProgress = hasNumeric ? (item?.inProgress ?? 0) : (item?.stato === 'in progress' ? 1 : 0);
      const nonAvviati = hasNumeric ? (item?.nonAvviati ?? 0) : (item?.stato === 'not started' ? 1 : 0);
      const total      = hasNumeric ? (item?.totale ?? (completed + inProgress + nonAvviati)) : 1;

      const name = item?.denominazione || item?.nomeGarage || item?.garageName || 'N/D';

      return {
        id: item?.garageId || item?._id || index,
        garage: name,
        regione: item?.regione || '—',
        citta: item?.citta || '—',

        completed,
        inProgress,
        nonAvviati,
        total,

        percentage: total > 0 ? (completed / total) : null,      // 0–1 (fallback)
        media: total > 0 ? (completed * 100) / total : null,     // 0–100 (se vuoi visualizzarla altrove)
        totAuto: item?.totAuto ?? item?.numero_auto ?? null,
      };
    });
  }, [val]);

  const pageSizeOptions = React.useMemo(() => {
    const all = rows.length || 1000;
    return Array.from(new Set([10, 25, 50, 100, all]));
  }, [rows.length]);

  return (
    <Paper sx={{ width: '100%' }}>
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id}
        initialState={{
          pagination: { paginationModel },
          columns: {
            columnVisibilityModel: {
              garage: true,
              regione: true,
              citta: true,
              completed: true,
              inProgress: true,
              nonAvviati: true,
              total: true,
              percentage: true,
              totAuto: true,
            },
          },
        }}
        pageSizeOptions={pageSizeOptions}
        density="compact"
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: {
            csvOptions: {
              fileName: `Depositi-Admin-${campaignName?.replace(/\s+/g, "_") || "Campagna"}`,
            },
          },
        }}
        sx={{ border: 0 }}
        localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
      />
    </Paper>
  );
}
